import clsx from 'clsx'
import type { CalendarResponse, EconomicEvent } from '../../types'

interface Props {
  data:     CalendarResponse | null
  loading?: boolean
  symbol?:  string
}

const IMPACT_STYLE: Record<string, string> = {
  High:   'bg-trading-red/20 text-trading-red border-trading-red/40',
  Medium: 'bg-trading-yellow/20 text-trading-yellow border-trading-yellow/40',
  Low:    'bg-trading-muted/10 text-trading-muted border-trading-muted/20',
}

const CCY_STYLE: Record<string, string> = {
  USD: 'text-trading-blue',
  EUR: 'text-trading-green',
  JPY: 'text-trading-yellow',
  GBP: 'text-trading-red',
  AUD: 'text-trading-green',
  CAD: 'text-trading-blue',
  CHF: 'text-trading-muted',
  NZD: 'text-trading-green',
}

const CURRENCIES = ['USD', 'EUR', 'JPY', 'GBP', 'AUD', 'CAD', 'CHF', 'NZD'] as const

// "3.4%", "256K", "-0.3", "1.2B" などを数値にパース
function parseEventValue(s: string): number | null {
  if (!s) return null
  const clean = s.trim().replace(/[%$,\s]/g, '')
  const match = clean.match(/^(-?\d+\.?\d*)([KMBTkmbt]?)$/)
  if (!match) return null
  const num = parseFloat(match[1])
  if (isNaN(num)) return null
  const mult: Record<string, number> = { K: 1e3, M: 1e6, B: 1e9, T: 1e12 }
  return num * (mult[match[2].toUpperCase()] ?? 1)
}

type Direction = 'up' | 'down' | null

// シンボルの基軸通貨・決済通貨を返す
function getSymbolCurrencies(symbol: string): { base: string | null; quote: string | null } {
  const sym = symbol.toUpperCase().replace('=X', '').replace('.T', '')
  for (const base of CURRENCIES) {
    if (!sym.startsWith(base)) continue
    const rest = sym.slice(base.length)
    for (const quote of CURRENCIES) {
      if (rest.startsWith(quote)) return { base, quote }
    }
    return { base, quote: null }
  }
  return { base: null, quote: null }
}

// イベント通貨がシンボルに関連しているか判定（USD は常に関連）
function isRelevantCurrency(symbol: string, currency: string): boolean {
  if (currency === 'USD') return true
  const { base, quote } = getSymbolCurrencies(symbol)
  return currency === base || currency === quote
}

// 予想値と前回値の乖離からシンボルへの価格影響方向を推論
function computeSymbolImpact(symbol: string, eventCurrency: string, forecast: string, previous: string): Direction {
  const f = parseEventValue(forecast)
  const p = parseEventValue(previous)
  if (f === null || p === null || f === p) return null

  const currencyBullish = f > p
  const { base, quote } = getSymbolCurrencies(symbol)

  if (base === null) {
    // 暗号通貨・株式: USD 強 = リスクオフ = 下落圧力
    if (eventCurrency !== 'USD') return null
    return currencyBullish ? 'down' : 'up'
  }

  if (eventCurrency === base) return currencyBullish ? 'up' : 'down'
  if (eventCurrency === quote) return currencyBullish ? 'down' : 'up'

  // USD が第三通貨の場合: USD 強 = グローバルリスクオフ = 非 USD ペアに下押し
  if (eventCurrency === 'USD') return currencyBullish ? 'down' : 'up'

  return null
}

export default function EconomicCalendar({ data, loading, symbol }: Props) {
  if (loading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-14 bg-trading-border rounded-lg" />
        ))}
      </div>
    )
  }

  if (!data) return null

  const { events, imminent } = data

  return (
    <div className="space-y-4">
      {/* 直近 1 時間警告 */}
      {imminent.length > 0 && (
        <div className="rounded-lg border border-trading-red/40 bg-trading-red/5 p-3">
          <p className="text-[10px] font-bold text-trading-red uppercase tracking-widest mb-2">
            ⚠ 1 時間以内の重要指標
          </p>
          <div className="space-y-1.5">
            {imminent.map((ev) => (
              <EventRow key={ev.id || ev.date_jst} event={ev} highlight symbol={symbol} />
            ))}
          </div>
        </div>
      )}

      {/* イベント一覧 */}
      <div className="rounded-lg border border-trading-border bg-trading-panel">
        <div className="px-3 py-2 border-b border-trading-border">
          <p className="text-[10px] text-trading-muted uppercase tracking-widest">
            今後 24 時間の経済指標（高・中インパクト）
          </p>
        </div>

        {events.length === 0 ? (
          <p className="text-xs text-trading-muted text-center py-6">
            予定されている重要指標はありません
          </p>
        ) : (
          <div className="divide-y divide-trading-border/50">
            {events.map((ev) => (
              <EventRow key={ev.id || ev.date_jst} event={ev} symbol={symbol} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function EventRow({
  event: ev,
  highlight,
  symbol,
}: {
  event: EconomicEvent
  highlight?: boolean
  symbol?: string
}) {
  const impactStyle = IMPACT_STYLE[ev.impact] ?? IMPACT_STYLE.Low
  const ccyColor    = CCY_STYLE[ev.currency] ?? 'text-trading-muted'

  const relevant  = symbol ? isRelevantCurrency(symbol, ev.currency) : false
  const direction = (symbol && relevant && ev.forecast && ev.previous)
    ? computeSymbolImpact(symbol, ev.currency, ev.forecast, ev.previous)
    : null

  // シンボルの短縮表記（表示用）
  const symLabel = symbol
    ? symbol.replace('=X', '').replace('.T', '')
    : ''

  return (
    <div
      className={clsx(
        'flex items-start gap-3 px-3 py-2.5',
        highlight && 'bg-trading-red/5',
        relevant && !highlight && 'bg-trading-blue/5 border-l-2 border-l-trading-blue/40',
      )}
    >
      {/* 日時 (JST) */}
      <div className="flex-none w-[68px]">
        <p className="text-[10px] font-mono text-trading-text">
          {ev.date_jst_str || '未定'}
        </p>
        <p className="text-[9px] text-trading-muted">JST</p>
      </div>

      {/* 通貨 */}
      <div className="flex-none w-[34px]">
        <p className={clsx('text-xs font-bold', ccyColor)}>{ev.currency}</p>
        <p className="text-[9px] text-trading-muted truncate">{ev.currency_ja}</p>
      </div>

      {/* 影響度 */}
      <div className="flex-none">
        <span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded border', impactStyle)}>
          {ev.impact_ja}
        </span>
      </div>

      {/* イベント名 + 予想/前回 */}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-trading-text truncate" title={ev.title_ja}>
          {ev.title_ja}
        </p>
        {(ev.forecast || ev.previous) && (
          <p className="text-[10px] text-trading-muted mt-0.5">
            {ev.forecast && <span>予想: {ev.forecast_ja || ev.forecast}　</span>}
            {ev.previous && <span>前回: {ev.previous_ja || ev.previous}</span>}
          </p>
        )}
      </div>

      {/* シンボル影響方向バッジ */}
      {relevant && (
        <div className="flex-none flex items-center">
          {direction === 'up' && (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-trading-green/20 text-trading-green border border-trading-green/40"
              title={`予想が前回を上回る場合、${symLabel} は上昇圧力`}
            >
              {symLabel} ↑
            </span>
          )}
          {direction === 'down' && (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-trading-red/20 text-trading-red border border-trading-red/40"
              title={`予想が前回を上回る場合、${symLabel} は下落圧力`}
            >
              {symLabel} ↓
            </span>
          )}
          {direction === null && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded bg-trading-muted/10 text-trading-muted border border-trading-muted/20"
              title="予想値・前回値から方向を判定できません"
            >
              {symLabel} —
            </span>
          )}
        </div>
      )}
    </div>
  )
}
